import { createContext, type PropsWithChildren, useContext, useState } from 'react'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { type QuoteAllowanceParams } from 'components/hooks/quotes/useCreateQuoteAllowances'
import useFlatAllowanceCalculations from 'components/hooks/quotes/useFlatAllowanceCalculations'
import { useQuoteCosts } from 'components/hooks/quotes/useQuoteCosts'
import { useQuoteExplorerState } from 'components/hooks/quotes/useQuoteExplorerState'
import useQuoteResultCosts from 'components/hooks/quotes/useQuoteResultCosts'
import { useQuotePricing } from 'components/quoting/quotes/pricing/QuotePricingProvider'
import {
  useGetQuotingQuoteResultsQuoteIdFlatAllowanceResults as useGetFlatAllowanceResults,
  useGetQuotingQuoteResultsQuoteIdPlanAvailability as useGetPlanAvailability,
  useGetQuotingQuoteResultsQuoteIdRecommendedBenchmarkResults as useGetRecommendedBenchmarkResults,
} from 'utils/web_api'
import { type QuotingPlanAvailability, type QuotingQuoteResult, type QuotingRecommendedQuoteResults } from 'utils/web_api.schemas'

import { useQuote } from '../QuoteProvider'
import { AllowanceStrategies, type AllowanceStrategy, type CoverageLevel, type CoverageLevelPill, type FiltersValues } from './types'
import { useProspect } from '../setup/ProspectProvider'

type SelfServeExplorerContextType = {
  allowanceStrategy: AllowanceStrategy
  setAllowanceStrategy: (strategy: AllowanceStrategy) => void
  filterValues: FiltersValues
  setFilterValues: (values: FiltersValues) => void
  resetFilterValues: () => void
  selectedCoverageLevel: CoverageLevel
  onSelectedCoverageLevelChange: (level: CoverageLevel) => void
  willEnrollEmployeeIds: Set<string>
  onWillEnrollEmployeeIdsChange: (employeeIds: Set<string>) => void
  willEnrollEmployees: QuotingQuoteResult[]
  showIneligibleEmployees: boolean
  setShowIneligibleEmployees: (show: boolean) => void
  ineligibleEmployees: QuotingQuoteResult[]
  totalMonthlyPlatformFee: number
  perEmployeeCost: number
  complianceFee: number
  employerPortionOfInsurance: number
  employeePortionOfInsurance: number
  totalEmployerCost: number
  currentQuoteResults: QuotingQuoteResult[]
  getPlanAvailabilityForEmployee: (employee: QuotingQuoteResult) => QuotingPlanAvailability | undefined
  getTotalPlansForEmployee: (employee: QuotingQuoteResult) => number
  quoteResultsByCoverageLevel?: QuotingRecommendedQuoteResults
  planAvailability: QuotingPlanAvailability[]
  quoteId: string
  isLoading: boolean
  hasError: boolean
  error: GrapeAPIErrorType | null
  getMonthlyAllowanceForEmployee: (employeeId: string) => number
  isAllowanceAffordable: (employeeId: string) => boolean
  getMinAffordableAllowanceForEmployee: (employeeId: string) => number
  allowances: QuoteAllowanceParams[]
  getEmployeeById: (employeeId: string) => QuotingQuoteResult | undefined
  hasLowestCostBenchmark: boolean
  hasBetterBenefitsBenchmark: boolean
  eligibleEmployees: QuotingQuoteResult[]
  isFlatRecommended: boolean
  coverageLevelPills: CoverageLevelPill[]
  isEmployeeStateUsingProjectedData: (employee: QuotingQuoteResult) => boolean
  isOnFinalWizardStep: boolean
  setIsOnFinalWizardStep: (isOnFinal: boolean) => void
  isUsingWizard: boolean
  setIsUsingWizard: (isUsing: boolean) => void
}

export const SelfServeExplorerContext = createContext<SelfServeExplorerContextType>({
  filterValues: {
    coverageLevel: 'best_fit',
    employerEmployeeContribution: 1,
    employerSpouseContribution: 0.5,
    employerChildContribution: 0.5,
  },
  setFilterValues: () => { },
  resetFilterValues: () => { },
  selectedCoverageLevel: 'best_fit',
  onSelectedCoverageLevelChange: () => { },
  willEnrollEmployeeIds: new Set(),
  onWillEnrollEmployeeIdsChange: () => { },
  willEnrollEmployees: [],
  showIneligibleEmployees: false,
  setShowIneligibleEmployees: () => { },
  ineligibleEmployees: [],
  totalMonthlyPlatformFee: 0,
  perEmployeeCost: 0,
  complianceFee: 0,
  employerPortionOfInsurance: 0,
  employeePortionOfInsurance: 0,
  totalEmployerCost: 0,
  quoteResultsByCoverageLevel: {
    lowest_cost: {
      benchmark: {
        metal_tier: '',
        coverage_percentile: '',
      },
      quote_results: [],
    },
    best_fit: {
      benchmark: {
        metal_tier: '',
        coverage_percentile: '',
      },
      quote_results: [],
    },
    better_benefits: {
      benchmark: {
        metal_tier: '',
        coverage_percentile: '',
      },
      quote_results: [],
    },
  },
  currentQuoteResults: [],
  isLoading: true,
  getPlanAvailabilityForEmployee: () => undefined,
  getTotalPlansForEmployee: () => 0,
  getMonthlyAllowanceForEmployee: () => 0,
  isAllowanceAffordable: () => false,
  getMinAffordableAllowanceForEmployee: () => 0,
  planAvailability: [],
  quoteId: '',
  hasError: false,
  error: null,
  allowances: [],
  getEmployeeById: () => undefined,
  allowanceStrategy: AllowanceStrategies.Smart,
  setAllowanceStrategy: () => { },
  hasLowestCostBenchmark: true,
  hasBetterBenefitsBenchmark: true,
  eligibleEmployees: [],
  isFlatRecommended: false,
  coverageLevelPills: [],
  isEmployeeStateUsingProjectedData: () => false,
  isOnFinalWizardStep: false,
  setIsOnFinalWizardStep: () => { },
  isUsingWizard: false,
  setIsUsingWizard: () => { },
})

export function useSelfServeExplorerContext () {
  return useContext(SelfServeExplorerContext)
}

export function SelfServeExplorerProvider ({
  children,
}: PropsWithChildren) {
  const { quote } = useQuote()
  const { prospect } = useProspect()
  const quoteId = quote!.id
  const [isOnFinalWizardStep, setIsOnFinalWizardStep] = useState(false)
  const [isUsingWizard, setIsUsingWizard] = useState(false)

  // Get all recommended benchmark results in a single API call
  const { data: quoteResultsByCoverageLevel, error: benchmarkResultsError, isPending: isBenchmarkResultsLoading } = useGetRecommendedBenchmarkResults<QuotingRecommendedQuoteResults>(
    quoteId,
    {
      query: {
        refetchOnWindowFocus: false,
        enabled: true,
      },
    }
  )

  const { data: flatAllowanceEmployees, error: flatAllowanceError, isPending: isLoadingFlatAllowanceEmployees } = useGetFlatAllowanceResults<QuotingQuoteResult[]>(
    quoteId,
    {
      query: {
        refetchOnWindowFocus: false,
        enabled: true,
      },
    }
  )

  const { data: planAvailability, isPending: isLoadingPlanAvailability } = useGetPlanAvailability<QuotingPlanAvailability[]>(
    quoteId,
    {
      query: {
        refetchOnWindowFocus: false,
        enabled: true,
      },
    }
  )

  const hasError = !!benchmarkResultsError || !!flatAllowanceError
  const error = benchmarkResultsError || flatAllowanceError

  // Show loading when we're still loading dependent data
  // Using isPending instead of isLoading because isPending is true for all query states, not just first load
  const isLoading = isBenchmarkResultsLoading || isLoadingFlatAllowanceEmployees || isLoadingPlanAvailability

  const {
    filterValues,
    setFilterValues,
    selectedCoverageLevel,
    setSelectedCoverageLevel,
    showIneligibleEmployees,
    setShowIneligibleEmployees,
    allowanceStrategy,
    setAllowanceStrategy,
    willEnrollEmployeeIds,
    onWillEnrollEmployeeIdsChange,
    willEnrollEmployees,
    eligibleEmployees,
    ineligibleEmployees,
    currentQuoteResults,
    isFlatRecommended: isFlatRecommendedValue,
    coverageLevelPills,
    hasLowestCostBenchmark,
    hasBetterBenefitsBenchmark,
    getEmployeeById,
    getPlanAvailabilityForEmployee,
    getTotalPlansForEmployee,
    getMonthlyAllowanceForEmployee,
    isAllowanceAffordable,
    getMinAffordableAllowanceForEmployee,
    resetFilterValues,
    allowances,
    isEmployeeStateUsingProjectedData,
  } = useQuoteExplorerState(
    quote,
    prospect,
    quoteResultsByCoverageLevel,
    flatAllowanceEmployees,
    planAvailability,
    isLoading
  )

  const flatAllowanceCosts = useFlatAllowanceCalculations(
    willEnrollEmployees,
    allowances
  )

  const dynamicAllowanceCosts = useQuoteResultCosts(
    willEnrollEmployees,
    filterValues.employerEmployeeContribution,
    filterValues.employerSpouseContribution,
    filterValues.employerChildContribution
  )

  const {
    employerPortionOfInsurance,
    employeePortionOfInsurance,
  } = useQuoteCosts(
    willEnrollEmployees,
    flatAllowanceCosts,
    dynamicAllowanceCosts,
    allowanceStrategy
  )

  // Dynamic pricing from backend
  const { pepmCost, quotePricing, isPending: isPricingLoading, getTotalCost } = useQuotePricing()
  const perEmployeeCost = pepmCost
  const complianceFee = quotePricing?.base_compliance_price ?? 0
  const totalMonthlyPlatformFee = getTotalCost(willEnrollEmployees.length)
  const totalEmployerCost = employerPortionOfInsurance + totalMonthlyPlatformFee

  const combinedIsLoading = isLoading || isPricingLoading

  return (
    <SelfServeExplorerContext.Provider
      value={{
        allowanceStrategy,
        setAllowanceStrategy,
        filterValues,
        setFilterValues,
        resetFilterValues,
        selectedCoverageLevel,
        onSelectedCoverageLevelChange: setSelectedCoverageLevel,
        willEnrollEmployeeIds,
        onWillEnrollEmployeeIdsChange,
        willEnrollEmployees,
        showIneligibleEmployees,
        setShowIneligibleEmployees,
        ineligibleEmployees,
        totalMonthlyPlatformFee,
        perEmployeeCost,
        complianceFee,
        employerPortionOfInsurance,
        employeePortionOfInsurance,
        totalEmployerCost,
        quoteResultsByCoverageLevel,
        planAvailability: planAvailability ?? [],
        isLoading: combinedIsLoading,
        currentQuoteResults,
        getPlanAvailabilityForEmployee,
        getTotalPlansForEmployee,
        getMonthlyAllowanceForEmployee,
        hasError,
        error: error as GrapeAPIErrorType | null,
        quoteId,
        allowances,
        getEmployeeById,
        hasLowestCostBenchmark,
        hasBetterBenefitsBenchmark,
        eligibleEmployees,
        isFlatRecommended: isFlatRecommendedValue,
        coverageLevelPills,
        isAllowanceAffordable,
        getMinAffordableAllowanceForEmployee,
        isEmployeeStateUsingProjectedData,
        isOnFinalWizardStep,
        setIsOnFinalWizardStep,
        isUsingWizard,
        setIsUsingWizard,
      }}
    >
      {children}
    </SelfServeExplorerContext.Provider>
  )
}
